import React, { useEffect, useRef, useState } from "react";
import { Button, Grid, Input, InputNumber, Message, Switch } from "@arco-design/web-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, OverwriteApi } from "../Api/AuthenticatedApi";
import EmailComposer from "../Editor/EmailComposer/EmailComposer";
import RecordNotFound from "../Components/RecordNotFound";

const { Row, Col } = Grid;

// Rich editor page for a single sequence step — the sequence counterpart to the
// broadcast/campaign mail-variant editors. Reuses the shared <EmailComposer> so
// steps get the same Easy Email visual editing + templates as everywhere else.
export default function SequenceStepEditor() {
    const { sequenceId, stepId } = useParams();
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const isNew = stepId === "new";

    const [loading, setLoading] = useState(!isNew);
    const [loadFailed, setLoadFailed] = useState(false);
    const [sequenceName, setSequenceName] = useState("");
    const [name, setName] = useState("");
    const [delayMinutes, setDelayMinutes] = useState(0);
    const [shouldRun, setShouldRun] = useState(true);
    const [stepOrder, setStepOrder] = useState(0);
    const [allTemplates, setAllTemplates] = useState([]);

    const composerRef = useRef(null);
    const [composerSeed, setComposerSeed] = useState({});
    const [seedKey, setSeedKey] = useState(0);

    const authFail = () => navigate("/login");
    const backToSequence = () => navigate(`/sequences/${sequenceId}/edit`);

    useEffect(() => {
        FetchApi(getAccessTokenSilently, `/api/sequences/${sequenceId}`,
            (data) => setSequenceName(data ? data.name : ""), authFail, () => {});
        // Templates power the "start from a template" picker.
        FetchApi(getAccessTokenSilently, "/api/email-templates/",
            (data) => setAllTemplates(data || []), authFail, () => {});

        if (isNew) {
            // Append after the existing steps. The composer lazy-initializes blank,
            // so no seed bump is needed (that would just remount Easy Email).
            FetchApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/steps/`,
                (data) => setStepOrder(Array.isArray(data) ? data.length : 0), authFail, () => {});
            return;
        }

        FetchApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/steps/${stepId}`,
            (data) => {
                setName("" + (data.name || ""));
                setDelayMinutes(data.delay_minutes || 0);
                setShouldRun(data.should_run !== false);
                setStepOrder(data.step_order || 0);
                setComposerSeed({
                    subject: data.mail_subject_template,
                    html: data.mail_html_template,
                    text: data.mail_text_template,
                    design: data.editor_design,
                });
                setSeedKey((k) => k + 1);
                setLoading(false);
            },
            authFail,
            () => { setLoading(false); setLoadFailed(true); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sequenceId, stepId, getAccessTokenSilently]);

    const save = () => {
        const content = composerRef.current ? composerRef.current.getContent() : {};
        const payload = {
            name,
            delay_minutes: Number(delayMinutes) || 0,
            should_run: shouldRun,
            step_order: stepOrder,
            ...content,
        };
        if (isNew) {
            PostApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/steps/`,
                () => { Message.success("Step added"); backToSequence(); }, authFail,
                (err) => Message.error((err && err.message) || "Failed to add step"), payload);
        } else {
            OverwriteApi(getAccessTokenSilently, `/api/sequences/${sequenceId}/steps/${stepId}`,
                () => { Message.success("Step saved"); backToSequence(); }, authFail,
                (err) => Message.error((err && err.message) || "Failed to save step"), payload);
        }
    };

    if (loadFailed) {
        return <RecordNotFound what="email" backTo={`/sequences/${sequenceId}/edit`} backLabel="Back to the sequence" />;
    }
    if (loading) {
        return <LoadingState />;
    }

    return (
        <Content>
            <SimpleBreadcrumbs
                elements={[
                    { name: "Sequences", path: "/sequences" },
                    { name: sequenceName, path: `/sequences/${sequenceId}/edit` },
                    { name: isNew ? "New step" : (name || "Step") },
                ]}
            />

            <SummaryCard title={isNew ? "New step" : "Edit step"}>
                <Row gutter={16}>
                    <Col span={24} style={{ marginBottom: 12 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
                            <div>
                                <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Step name</div>
                                <Input style={{ width: 260 }} value={name} placeholder="e.g. Welcome email" onChange={setName} />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Delay (minutes)</div>
                                <InputNumber style={{ width: 160 }} min={0} value={delayMinutes} onChange={(v) => setDelayMinutes(v || 0)} />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Active</div>
                                <Switch checked={shouldRun} onChange={setShouldRun} />
                            </div>
                        </div>
                    </Col>

                    <Col span={24}>
                        <EmailComposer
                            ref={composerRef}
                            initial={composerSeed}
                            seedKey={seedKey}
                            templates={allTemplates}
                            enablePersonalize
                        />
                    </Col>

                    <Col span={24} style={{ marginTop: 16 }}>
                        <Button type="primary" onClick={save} style={{ marginRight: 8 }}>
                            {isNew ? "Add step" : "Save step"}
                        </Button>
                        <Button onClick={backToSequence}>Cancel</Button>
                    </Col>
                </Row>
            </SummaryCard>
        </Content>
    );
}
